import { useCallback, useEffect, useRef, useState } from 'react';

import type { NotificationResponse, UserNotification } from '../types';

export function NotificationCenter() {
  const [opened, setOpened] = useState(false);
  const [items, setItems] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/notifications', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;
      const body = (await response.json()) as NotificationResponse;
      setItems(body.notifications);
      setUnreadCount(body.unreadCount);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 30_000);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  useEffect(() => {
    if (!opened) return;
    const close = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setOpened(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [opened]);

  async function markRead(notification: UserNotification) {
    if (!notification.read) {
      setItems((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, read: true } : item)),
      );
      setUnreadCount((count) => Math.max(0, count - 1));
      await fetch(`/api/v1/notifications/${notification.id}/read`, { method: 'PATCH' });
    }
    setOpened(false);
    document
      .querySelector(notification.cupSlug ? '#copa' : '#arena')
      ?.scrollIntoView({ behavior: 'smooth' });
  }

  async function markAllRead() {
    setItems((current) => current.map((item) => ({ ...item, read: true })));
    setUnreadCount(0);
    await fetch('/api/v1/notifications/read-all', { method: 'POST' });
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpened((value) => !value)}
        className="relative grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/5 text-zinc-300 backdrop-blur hover:border-fuchsia-200/30 hover:text-white"
        aria-label={unreadCount ? `${unreadCount} avisos sin leer` : 'Avisos'}
        aria-expanded={opened}
      >
        <span aria-hidden="true">♢</span>
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-fuchsia-300 px-1 text-[0.58rem] font-black leading-4 text-zinc-950">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {opened && (
        <div className="absolute right-0 top-12 z-50 w-[min(22rem,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/95 shadow-2xl shadow-black/60 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
            <div>
              <strong className="block text-sm text-white">Avisos de la arena</strong>
              <span className="text-[0.65rem] text-zinc-500">Retos, inicios y resultados</span>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-[0.65rem] text-violet-200 hover:text-white"
              >
                Marcar todo leído
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-center text-xs text-zinc-600">Cargando avisos…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-zinc-600">
                Aquí aparecerán tus retos y resultados.
              </p>
            ) : (
              items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => void markRead(item)}
                  className={`block w-full border-b border-white/5 px-4 py-3 text-left hover:bg-white/[0.04] ${item.read ? 'opacity-60' : 'bg-fuchsia-300/[0.035]'}`}
                >
                  <span className="flex items-start gap-2">
                    {!item.read && (
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-fuchsia-300" />
                    )}
                    <span>
                      <strong className="block text-xs text-zinc-200">{item.title}</strong>
                      <span className="mt-1 block text-xs leading-5 text-zinc-500">
                        {item.body}
                      </span>
                      <time className="mt-1 block text-[0.6rem] text-zinc-700">
                        {formatNotificationDate(item.createdAt)}
                      </time>
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatNotificationDate(value: string) {
  return new Intl.DateTimeFormat('es', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
